import type { CarProfile } from '../cars/CarProfile';
import { MockTelemetryProvider, type MockDriveMode } from '../telemetry/MockTelemetryProvider';
import { GeolocationTelemetryProvider, type GpsInfo } from '../telemetry/GeolocationTelemetryProvider';
import { RemoteTelemetryProvider, type RemoteInfo } from '../telemetry/RemoteTelemetryProvider';
import type { TelemetryFrame, TelemetryProvider } from '../telemetry/TelemetryProvider';
import { VirtualDrivetrain, type DrivetrainState, type ShiftMode, type DriveStyle } from '../drivetrain/VirtualDrivetrain';
import { AudioEngine, type AudioStatus } from '../audio/AudioEngine';
import { KeyboardDriver } from '../input/KeyboardDriver';
import { ShiftInput } from '../input/ShiftInput';
import { requestWakeLock, releaseWakeLock, isWakeLockActive } from '../utils/wakeLock';
import { loadSettings, saveSettings, roomFromUrl, type TelemetrySource, type DriveStyleSetting } from '../utils/settings';
import { capDemoLeft, isAdmin, getAdminToken } from '../utils/access';
import { requestGrant, type Grant } from '../utils/grant';
import { pauseOffline } from '../utils/offline';
import { LapTimer, type LapState } from '../lap/LapTimer';
import { GearLearner, type LearnState } from '../telemetry/GearLearner';
import { CIRCUITS } from '../lap/circuits';

/**
 * Session — wires telemetry → drivetrain → audio for one car and runs the
 * real-time loop. The UI only reads `state`/`getStatus()` and calls the few
 * commands below. One session lives per selected car.
 */

export interface SessionStatus {
  /** pit-limiter cap in km/h (null = off) */
  pitCap?: number | null;
  /** raw telemetry speed (uncapped — the pit page turns red when THIS exceeds the cap) */
  rawSpeedKmh?: number;
  /** WIPER panel text (two lines) while it is showing, else null */
  wiperMsg?: string | null;
  /** WET from the car's wiper (CAN); null = unknown */
  wet?: boolean | null;
  /** headlights on (from CAN; null = unknown, sim) */
  lightsOn?: boolean | null;
  /** real TPMS pressures [FL, FR, RL, RR] bar (CAN) */
  tires?: number[] | null;
  source: TelemetrySource;
  telemetryOk: boolean;
  telemetrySource: string;
  telemetryHz: number;
  telemetryQuality: string;
  gps: GpsInfo;
  remote: RemoteInfo;
  driveMode: MockDriveMode;
  gearbox: ShiftMode;
  driveStyle: DriveStyleSetting;
  /** style actually applied (auto resolves to eco/sport from how you drive) */
  resolvedStyle: DriveStyle;
  topSpeedKmh: number;
  lapRunning: boolean;
  lapHasData: boolean;
  audio: AudioStatus;
  lap: LapState;
  mediaSession: boolean;
  wakeLock: boolean;
  started: boolean;
  /** ENG START state */
  engine: EngineRunState;
  /** server verdict once the engine has been started: full / demo / none (demo used up); null = not asked yet */
  entitlement: 'full' | 'demo' | 'none' | null;
}

const TELEMETRY_TIMEOUT_MS = 1500;
const HOLD_DECAY_KMH_PER_S = 12;

export type EngineRunState = 'off' | 'cranking' | 'running' | 'stopping';
/** rolling faster than this with the engine off → it starts by itself (GPS / CAN) */
/** what the learner has worked out about the driver's own gearbox, kept between drives */
const GEARS_KEY = 'roardrive:gears:v1';
const AUTO_IGNITION_KMH = 5;
/** the engine can only be switched off below this speed */
const STANDSTILL_FOR_STOP_KMH = 3;

/** gearing per selected car (Marc's anchors): a 201 km/h car gets 1st = 67; a car whose top reaches the
 *  REAL box's 296 gets the real Porsche gearing untouched; everything between interpolates smoothly.
 *  firstScaled = first·(top/last)^1.44 (67 at 201, 117 at 296), then log-space through that point. */
export function scaleGearing(profile: CarProfile, topKmh: number): number[] {
  const tops = profile.gearTopSpeedsKmh;
  const last = tops[tops.length - 1];
  if (profile.realGearing || topKmh >= last) return tops.slice();
  const first = tops[0];
  const firstScaled = first * Math.pow(topKmh / last, 1.44);
  const beta = Math.log(firstScaled / topKmh) / Math.log(first / last);
  return tops.map((v) => Math.round(topKmh * Math.pow(v / last, beta)));
}

export class Session {
  private baseProfile!: CarProfile;
  readonly profile: CarProfile;
  readonly mock = new MockTelemetryProvider();
  readonly gps = new GeolocationTelemetryProvider();
  readonly remote: RemoteTelemetryProvider;
  readonly drivetrain: VirtualDrivetrain;
  readonly audio: AudioEngine;
  /** server-signed entitlement for this car (null until asked / offline with nothing cached) */
  grant: Grant | null = null;
  /** ENG START state machine: off → cranking → running → stopping → off */
  engine: EngineRunState = 'off';
  private wiperMsg: { text: string; until: number } | null = null;
  private lightsOn: boolean | null = null;
  private tiresBar: number[] | null = null;
  /** WET from the real wiper (CAN); null = the dash key decides */
  private wetFromCar: boolean | null = null;
  private wasLaunch = false;
  private ignitionTimers: ReturnType<typeof setTimeout>[] = [];
  readonly keyboard: KeyboardDriver;
  readonly shiftInput: ShiftInput;
  readonly log: string[] = [];
  readonly lapTimer = new LapTimer(CIRCUITS);

  state: DrivetrainState;
  private source: TelemetrySource = 'sim';
  private readonly gearLearner: GearLearner = (() => {
    try {
      const raw = localStorage.getItem(GEARS_KEY);
      if (raw) return GearLearner.fromJSON(JSON.parse(raw));
    } catch { /* private mode or an older shape */ }
    return new GearLearner();
  })();
  private gearsSavedAt = 0;
  private lastEngineRpmAt = 0;
  private followMyGears = loadSettings().followMyGears;
  /** the same learner, fed by the SIMULATOR — the only way to watch it work without a dongle in a car */
  private readonly simLearner = new GearLearner();
  private telemetry: TelemetryProvider;
  private unsubscribe: (() => void) | null = null;
  private latest: TelemetryFrame | null = null;
  private lastFrameAt = 0;
  /** CAN mode: the browser's own GPS as a gap-filler for speed (Marc: the CAN stutters over the relay; the GPS is steady) */
  private latestGps: TelemetryFrame | null = null;
  private lastRelayGpsAt = 0;
  private lastGpsAt = 0;
  private gpsSideUnsub: (() => void) | null = null;
  private speedFrom: 'can' | 'gps' = 'can';
  private frameTimes: number[] = [];
  private heldSpeed = 0;
  /** the road speed as measured, before the pit limiter caps what the engine is allowed to hear */
  private rawSpeed: number | null = null;
  /** DEMO pit limiter: speed fed to the drivetrain is capped here (null = no cap) */
  private demoCapKmh: number | null = null;
  private manualCapKmh: number | null = null;
  /** effective pit-limiter cap: the driver's PIT button wins over the demo cap */
  private get speedCapKmh(): number | null { return this.manualCapKmh ?? this.demoCapKmh; }
  private telemetryOk = false;
  private mediaSessionOk = false;
  private started = false;
  private running = false;
  private lastTick = 0;
  private rafId = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private touchPedal = 0;
  private touchBrake = 0;
  private driveStyleSetting: DriveStyleSetting = 'auto';
  private aggressiveness = 0.5; // auto style detector state
  private resolvedStyle: DriveStyle = 'sport';
  private topSpeedKmh = 201;

  constructor(profile: CarProfile) {
    const st = loadSettings();
    // scale the virtual gearbox so top gear peaks at the REAL Tesla's top speed
    this.topSpeedKmh = st.vehicleTopSpeedKmh;
    // virtual gearbox: scaled to the Tesla's top speed unless the car ships its REAL ratios
    // progressive scaling: LOW gears stay REAL (Marc calibrated 1st: 4771 rpm @ 59 km/h) and only the top
    // compresses so 6th still peaks at the selected Tesla's top speed
    this.baseProfile = profile;
    // the virtual car can never outrun its own real box: a Plaid tops at the GT3 R's 296
    this.topSpeedKmh = Math.min(this.topSpeedKmh, profile.gearTopSpeedsKmh[profile.gearTopSpeedsKmh.length - 1]);
    // ICE car (Marc): the virtual box shifts where the REAL car shifts — its own per-gear speeds replace the ladder
    const ice = st.vehicleKind === 'ice' && st.vehicleGearTopsKmh && st.vehicleGearTopsKmh.length >= 3 ? st.vehicleGearTopsKmh.slice() : null;
    // GEAR RATIO = the Porsche (default): the engine's own real ladder, whatever the driver's car
    const ladder = st.gearing === 'own' ? (ice ?? scaleGearing(profile, this.topSpeedKmh)) : profile.gearTopSpeedsKmh.slice();
    this.profile = { ...profile, gearTopSpeedsKmh: ladder };
    profile = this.profile;
    this.drivetrain = new VirtualDrivetrain(profile);
    this.audio = new AudioEngine(profile);
    this.audio.onHealth = (line) => {
      this.addLog(line);
      // ...and to the server, so the numbers can be read off a desk while the admin drives
      if (isAdmin()) {
        void fetch('/api/diag/health', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken() ?? ''}` }, body: JSON.stringify({ car: profile.id, build: __ROAR_BUILD__, line }) }).catch(() => undefined);
      }
    };
    this.state = this.drivetrain.getState();
    this.telemetry = this.mock;
    // the simulator's ceiling is the LADDER's top gear, not the car-in-settings top speed: with the
    // Porsche's own box (the default) the ladder ends at 285 and the sim was stopping at the 200 that
    // lives in settings for an electric car (Marc). setGearing / setGearTops already did this; nobody
    // did it on the way in.
    this.mock.setTopSpeedKmh(ladder.length ? ladder[ladder.length - 1] : this.topSpeedKmh);
    this.keyboard = new KeyboardDriver({
      onShiftUp: () => this.shift(1),
      onShiftDown: () => this.shift(-1),
      onToggleGearbox: () => this.toggleGearbox(),
      onToggleDrive: () => this.toggleDriveMode(),
      onIgnition: () => void this.ignition(),
      onLaunch: () => this.toggleLaunch(),
    });
    this.shiftInput = new ShiftInput({
      onShiftUp: () => this.shift(1),
      onShiftDown: () => this.shift(-1),
      onShiftAuto: () => this.shiftAuto(),
      onAction: (a) => this.addLog(`mediasession: ${a}`),
    });
    this.drivetrain.on((e) => {
      this.audio.handleEvent(e, this.state);
      if (e.type === 'shiftUp' || e.type === 'shiftDown') this.addLog(`${e.type} ${e.fromGear}→${e.toGear} @ ${Math.round(e.rpm)} rpm`);
    });
    const s = loadSettings();
    this.driveStyleSetting = s.driveStyle;
    this.applyDriveStyle();
    const room = roomFromUrl() ?? s.relayRoom;
    if (room !== s.relayRoom) saveSettings({ relayRoom: room });
    this.remote = new RemoteTelemetryProvider(room);
    this.gps.onPosition((p) => {
      if (performance.now() - this.lastRelayGpsAt < 1500) return; // RaceBox 25 Hz has the lap timer — the browser's 1 Hz stands down
      const before = this.lapTimer.getState().lapCount;
      this.lapTimer.update({ lat: p.lat, lon: p.lon }, p.t);
      const st = this.lapTimer.getState();
      if (st.lapCount > before) this.addLog(`lap ${st.lapCount}: ${st.lastS?.toFixed(2)}s${st.bestS === st.lastS ? ' (best)' : ''}`);
    });
    this.remote.onWheel((w) => {
      // real steering-wheel scroll button seen on the CAN bus → paddles
      if (w === 'press' || w === 'tiltRight' || w === 'tick+') this.shift(1);
      else if (w === 'tiltLeft' || w === 'tick-') this.shift(-1);
      this.addLog(`can wheel: ${w}`);
    });
    this.remote.onBody((e, body) => {
      // real body signals from the Enhauto/Panda CAN bridge → door clips, WIPER panel, headlight icon
      if (e.startsWith('door:')) { this.audio.playShot(e.endsWith(':open') ? 'doorOpen' : 'doorShut', 0.9); }
      else if (e === 'lights:on' || e === 'lights:off') this.lightsOn = e === 'lights:on';
      else if (e.startsWith('wiper:')) { this.wetFromCar = body.wiper !== 'off'; this.showWiper(({ off: 'OFF', one: 'ONE WIPE', int: 'INTERMITTENT', slow: 'SLOW', fast: 'FAST' } as Record<string, string>)[body.wiper] ?? body.wiper.toUpperCase()); }
      this.addLog(`can body: ${e}`);
    });
    this.audio.setVolume(s.volume);
    this.drivetrain.setMode(s.gearbox);
    this.source = s.source;   // the SIM is free for everyone now (Marc); only its instrumented screen is admin
  }

  /** Starts telemetry + loop (no audio yet — that needs a gesture). */
  run(): void {
    if (this.running) return;
    this.running = true;
    this.attachTelemetry(this.source);
    this.keyboard.attach();
    this.lastTick = performance.now();
    this.scheduleLoop();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.addLog(`session ready · ${this.profile.manufacturer} ${this.profile.name} · ${navigator.userAgent.slice(0, 80)}`);
  }

  /** Call from a click/touch: enters the cockpit — audio graph up (ambience), engine OFF until ENG START. */
  async startEngine(): Promise<void> {
    this.started = true;
    // the loader wants the car's LTE for itself: whatever the offline download was fetching stops here
    // (this drive fills the same cache anyway), and the bay asks for the rest again on the way out
    pauseOffline();
    this.drivetrain.setEngineState('off');
    this.audio.setEngineRunning(false, 0.01);
    // the server decides FULL vs DEMO and signs the audio downloads
    this.grant = await requestGrant(this.profile.id);
    this.audio.setGrantToken(this.grant?.token ?? '', this.grant?.mode === 'demo' ? this.grant.key ?? '' : '');
    // the server's ledger wins over this browser's: a wiped page does not get a fresh two minutes
    if (this.grant?.mode === 'demo' && typeof this.grant.leftS === 'number') capDemoLeft(this.profile.id, this.grant.leftS);
    this.addLog(`entitlement: ${this.grant?.mode ?? 'unknown (offline?)'}`);
    const audioP = this.audio.start();
    this.mediaSessionOk = await this.shiftInput.attach(`${this.profile.manufacturer} ${this.profile.name}`, 'ROARDRIVE');
    void requestWakeLock();
    await audioP;
    const st = this.audio.getStatus();
    this.addLog(`audio ${st.state} · ${st.sampleRate} Hz · base latency ${st.baseLatencyMs} ms · output ${st.outputLatencyMs ?? '?'} ms`);
    if (st.report) {
      this.addLog(`layers: ${st.report.loopsReal} real (${st.report.format ?? 'no decodable format'}) / ${st.report.loopsProcedural} procedural · one-shots ${st.report.oneShotsReal}/${st.report.oneShotsProcedural}`);
    }
    this.addLog(`mediasession ${this.mediaSessionOk ? 'attached' : 'unavailable'} · wakelock ${isWakeLockActive()}`);
  }

  /** After a purchase: fetch a fresh grant (FULL) without restarting the audio. */
  async refreshGrant(): Promise<Grant | null> {
    this.grant = await requestGrant(this.profile.id, true);
    this.audio.setGrantToken(this.grant?.token ?? '', this.grant?.mode === 'demo' ? this.grant.key ?? '' : '');
    this.addLog(`entitlement: ${this.grant?.mode ?? 'unknown'}`);
    return this.grant;
  }

  /** Dev/harness: feed a telemetry frame directly (SIM source) — lets a script replay a real run. */
  private scripted = false;
  scriptFrame(speedKmh: number, pedal: number, brake = 0): void {
    if (!this.scripted) { this.scripted = true; this.unsubscribe?.(); this.unsubscribe = null; this.telemetry.stop(); this.addLog('telemetry → script'); }
    const now = performance.now();
    const prev = this.latest;
    const accel = prev && this.lastFrameAt ? ((speedKmh - prev.speedKmh) / 3.6) / Math.max(0.01, (now - this.lastFrameAt) / 1000) : 0;
    this.latest = { speedKmh, accelMs2: accel, pedal, brake, source: 'script' } as typeof this.latest;
    this.lastFrameAt = now;
  }

  /** WIPER panel on the dash (real wiper stalk via CAN, or the WET/DRY key): shows for 3 s. */
  showWiper(text: string): void {
    this.wiperMsg = { text, until: performance.now() + 3000 };
  }

  /** LAUNCH: arms only at standstill with the engine running; releases once the car moves. */
  toggleLaunch(): boolean {
    const on = this.drivetrain.setLaunch(!this.drivetrain.isLaunch());
    return on;
  }

  /** ENGINE START / STOP button: starts when off; stops only with the car standing still. */
  async ignition(): Promise<boolean> {
    if (!this.started) await this.startEngine();
    if (this.engine === 'off') { this.engineStart(); return true; }
    if (this.engine === 'running') {
      if (this.state.speedKmh > STANDSTILL_FOR_STOP_KMH) { this.addLog('ENG STOP ignored — car is moving'); return false; }
      this.engineStop();
      return true;
    }
    return false;
  }

  private clearIgnitionTimers(): void {
    for (const t of this.ignitionTimers) clearTimeout(t);
    this.ignitionTimers = [];
  }

  private starterHandle: { duration: number; stop: (fadeS?: number) => void } | null = null;
  /** wall-clock when the long real start clip wants the voice to take over (0 = short clip / done) */
  private starterEndsAt = 0;
  private fired = false;
  private stopOnRelease = false;

  private static lockCh: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('roardrive:engine-lock') : null;
  private lockId = Math.random().toString(36).slice(2);

  private engineStart(): void {
    // ONE engine per browser: any other tab running the engine shuts down (a background tab kept
    // playing once — two engines a few rpm apart beat in the room and read as a broken sound)
    if (Session.lockCh) {
      Session.lockCh.postMessage({ type: 'start', id: this.lockId });
      Session.lockCh.onmessage = (ev) => {
        if (ev.data?.type === 'start' && ev.data.id !== this.lockId && (this.engine === 'running' || this.engine === 'cranking')) {
          this.addLog('engine stopped: another tab started its engine');
          void this.stopEngine();
        }
      };
    }
    this.clearIgnitionTimers();
    this.engine = 'cranking';
    this.fired = false;
    this.addLog('ENG START (cranking)');
    // THE SHOWROOM GOES FIRST, AND LEAVES A HOLE (Marc: "cuando aprietas el start up, la voz se calla
    // y LUEGO se enciende… deja unas décimas en silencio para que se aprecie el arranque"). The music
    // and the storyteller used to be fading out UNDER the crank, so the one moment the car comes alive
    // arrived on top of somebody talking. Now the ignition waits for the room to go quiet.
    const hushS = this.audio.hushShowroom();
    if (hushS > 0) { this.ignitionTimers.push(setTimeout(() => this.crank(), hushS * 1000)); return; }
    this.crank();
  }

  /** the ignition proper: the starter clip, the catch, and the voice coming up under it */
  private crank(): void {
    // the real start Marc called "lo ideal" (Onboard Run_03, 4.6 s): crank ~0.9 s → catches and falls
    // STRAIGHT to idle (no high idle) → real wandering idle; the voice fades in under the tail.
    // Marc: "pon ese puto audio entero" — the clip is the COMPLETE Run_09 start + real idle (~32 s).
    // The granular voice stays closed while it plays; we hand over at the tail, or the moment you
    // drive off / touch the pedal (see starterHandover in the frame loop).
    // the crank sat well under the engine that follows it (Marc: 'el starter suena bajito, debería
    // sonar mucho más alto') — it is the moment the car comes alive, it should land
    // …and its tail now ducks under the idle that emerges beneath it, instead of simply stopping: the two
    // sounds overlap for the best part of a second so the car never goes quiet between them (Marc: 'el
    // motor arranca, luego silencio, y luego el clip del idle no es realista — se tienen que fusionar')
    // WHERE THE CLIP CATCHES IS THE CLIP'S BUSINESS, NOT A CONSTANT. 1.15 s was the 911's Run_03 and
    // every car since has its own recording: the 812 barks at 0.64 s, the 458 at 1.06 (CarProfile.start).
    const st = this.profile.start;
    const fireAt = st?.fireAtS ?? (this.audio.shotLength('starter') > 0 ? Math.min(1.0, Math.max(0.3, this.audio.shotLength('starter') - 0.8)) : 0.9);
    this.starterHandle = this.audio.playShot('starter', 1.9, { at: fireAt + (st?.duckAfterS ?? 0.15), gain: 0.55, over: 0.9 });
    const crankS = this.starterHandle.duration;
    // hand over 4 s before the clip ends: the voice sneaks in UNDER the clip's own real idle over a long
    // crossfade — the old 2 s window swapped fast enough to read as "two audios" (Marc)
    this.starterEndsAt = crankS > 6 ? performance.now() + (crankS - 4.0) * 1000 : 0;
    this.drivetrain.setEngineState('cranking');
    // real cabin clip: it fires at ~1.0 s and lands straight on idle → engine ON at idle (no flare),
    // idle loop fades in right under the catch, clip tail fades out: one continuous sound
    this.ignitionTimers.push(setTimeout(() => {
      this.fired = true;
      this.audio.playShot('startup', 1);
      // land ON idle, not on a 1.9x flare: from 3600 rpm the engine has to fall for a second before the
      // idle bed is allowed in at all, and that fall IS the gap Marc hears between the crank and the idle
      // …unless the car's own recording says otherwise: a cold Ferrari catches high and holds a fast
      // idle for seconds, and the voice has to be doing the same thing under the clip or they beat.
      this.drivetrain.setEngineState('on', st?.catchRpm ?? this.profile.idleRpm * 1.12);
      // long real clip → the voice waits for the handover; short/missing clip → open right away
      if (this.starterEndsAt === 0) this.audio.setEngineRunning(true, 0.45);
      this.ignitionTimers.push(setTimeout(() => { this.engine = 'running'; this.addLog('engine running'); }, 400));
    }, fireAt * 1000));
  }

  /** START/STOP pressed: begins cranking (hold ≥ ~1 s to catch) or arms a stop while running. */
  starterPress(): void {
    if (!this.started) { void this.startEngine().then(() => this.starterPress()); return; }
    this.stopOnRelease = false;
    if (this.engine === 'off') this.engineStart();
    else if (this.engine === 'running') this.stopOnRelease = true;
  }

  /** START/STOP released: too early → the starter just 'crunks' and the engine does not catch. */
  starterRelease(): void {
    if (this.engine === 'cranking' && !this.fired) {
      this.clearIgnitionTimers();
      this.starterHandle?.stop(0.05);
      this.starterHandle = null;
      this.starterEndsAt = 0;
      // real false start: it catches for a moment and dies
      this.audio.playShot('falsestart', 0.9);
      this.engine = 'off';                       // button logic: it is OFF right away (you can try again)
      this.drivetrain.setEngineState('on');      // needle: catches for a moment…
      this.ignitionTimers.push(setTimeout(() => { if (this.engine === 'off') this.drivetrain.setEngineState('off'); }, 700)); // …and dies
      this.addLog('starter released — false start');
      return;
    }
    if (this.stopOnRelease && this.engine === 'running') {
      this.stopOnRelease = false;
      if (this.state.speedKmh > STANDSTILL_FOR_STOP_KMH) { this.addLog('ENG STOP ignored — car is moving'); return; }
      this.engineStop();
    }
  }

  private engineStop(): void {
    this.clearIgnitionTimers();
    // the long real start/idle clip must die with the engine (it kept playing over ENG STOP)
    this.starterHandle?.stop(0.25);
    this.starterHandle = null;
    this.starterEndsAt = 0;
    this.engine = 'stopping';
    this.addLog('ENG STOP');
    this.audio.playShot('shutoff', 1);
    this.drivetrain.setEngineState('off');
    this.audio.setEngineRunning(false, 0.35);
    this.ignitionTimers.push(setTimeout(() => { this.engine = 'off'; this.addLog('engine off'); }, 1400));
  }

  async stopEngine(): Promise<void> {
    this.started = false;
    this.clearIgnitionTimers();
    this.starterHandle?.stop(0.1);
    this.starterHandle = null;
    this.starterEndsAt = 0;
    this.engine = 'off';
    this.drivetrain.setEngineState('off');
    this.shiftInput.detach();
    releaseWakeLock();
    await this.audio.stop();
    this.addLog('audio stopped');
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.keyboard.detach();
    this.unsubscribe?.();
    this.telemetry.stop();
    void this.stopEngine();
  }

  // ---- commands ----------------------------------------------------------

  /**
   * The Tesla's wheel button is ONE button, and it was always an upshift. A real driver presses it to
   * go up while they are pulling and down while they are slowing, so that is what it does: the car's own
   * acceleration decides the direction (Marc, in the car).
   */
  shiftAuto(): void {
    const st = this.state;
    const accel = st.accelMs2;
    const slowing = st.brake > 0.05 || accel < -0.25 || (st.throttleIntent < 0.05 && accel < -0.05);
    this.shift(slowing ? -1 : 1);
    this.addLog(`wheel shift ${slowing ? 'down' : 'up'} (a=${accel.toFixed(2)} m/s², gas ${st.throttleIntent.toFixed(2)}, brake ${st.brake.toFixed(2)})`);
  }

  shift(dir: 1 | -1): void {
    if (this.drivetrain.getMode() === 'auto') {
      this.drivetrain.setMode('manual');
      saveSettings({ gearbox: 'manual' });
    }
    this.drivetrain.requestShift(dir);
  }

  toggleGearbox(): ShiftMode {
    const m = this.drivetrain.toggleMode();
    saveSettings({ gearbox: m });
    this.addLog(`gearbox ${m}`);
    return m;
  }

  setDriveStyle(style: DriveStyleSetting): void {
    this.driveStyleSetting = style;
    saveSettings({ driveStyle: style });
    this.applyDriveStyle();
    this.addLog(`drive style ${style}`);
  }

  cycleDriveStyle(): DriveStyleSetting {
    const order: DriveStyleSetting[] = ['auto', 'eco', 'sport'];
    const next = order[(order.indexOf(this.driveStyleSetting) + 1) % order.length];
    this.setDriveStyle(next);
    return next;
  }

  private applyDriveStyle(): void {
    const resolved: DriveStyle =
      this.driveStyleSetting === 'auto' ? (this.aggressiveness > 0.5 ? 'sport' : 'eco') : this.driveStyleSetting;
    if (resolved !== this.resolvedStyle) this.addLog(`style → ${resolved}${this.driveStyleSetting === 'auto' ? ' (auto)' : ''}`);
    this.resolvedStyle = resolved;
    this.drivetrain.setDriveStyle(resolved);
  }

  setTopSpeed(kmh: number): void {
    kmh = Math.min(kmh, this.baseProfile.gearTopSpeedsKmh[this.baseProfile.gearTopSpeedsKmh.length - 1]);
    this.topSpeedKmh = kmh;
    this.mock.setTopSpeedKmh(kmh);
    // mutate in place — the drivetrain reads this array every tick
    this.replaceGearTops(scaleGearing(this.baseProfile, kmh));
    saveSettings({ vehicleTopSpeedKmh: kmh });
    this.addLog(`top speed ${kmh} km/h`);
  }

  /** GEAR RATIO switch: 'porsche' = the engine's own ladder · 'own' = the driver's car (EV top speed or ICE gears). */
  setGearing(mode: 'porsche' | 'own'): void {
    const st = loadSettings();
    if (mode === 'porsche') {
      this.replaceGearTops(this.baseProfile.gearTopSpeedsKmh);
      this.mock.setTopSpeedKmh(this.baseProfile.gearTopSpeedsKmh[this.baseProfile.gearTopSpeedsKmh.length - 1]);
      this.addLog('gearbox: the engine\'s own ladder');
    } else if (st.vehicleKind === 'ice' && st.vehicleGearTopsKmh && st.vehicleGearTopsKmh.length >= 3) {
      this.setGearTops(st.vehicleGearTopsKmh);
    } else {
      this.setGearTops(null);
    }
  }

  /** ICE: use the driver's real per-gear speeds (null → back to the EV scaling of the current top speed). */
  setGearTops(tops: number[] | null): void {
    if (tops && tops.length >= 3) {
      this.replaceGearTops(tops);
      this.mock.setTopSpeedKmh(tops[tops.length - 1]);
      this.addLog(`gearbox: ${tops.length} gears, ${tops.join('/')} km/h`);
    } else {
      this.replaceGearTops(scaleGearing(this.baseProfile, this.topSpeedKmh));
      this.mock.setTopSpeedKmh(this.topSpeedKmh);
    }
  }

  private replaceGearTops(tops: number[]): void {
    // the SIM has to be able to reach the top of the ladder it is given (Marc: it topped out at 200 while
    // the box was geared for 285, because the mock kept the vehicle's own top speed from settings and only
    // setGearing/setGearTops ever moved it). One place owns it: whoever sets the ladder sets the ceiling.
    if (tops.length) this.mock.setTopSpeedKmh(tops[tops.length - 1]);
    const arr = this.profile.gearTopSpeedsKmh;   // same array the drivetrain holds
    arr.length = 0;
    arr.push(...tops);
  }

  cycleTopSpeed(): number {
    const presets = [201, 233, 262, 322]; // M3 LR · MY LR · M3 Performance · S Plaid
    const next = presets[(presets.indexOf(this.topSpeedKmh) + 1) % presets.length] ?? 201;
    this.setTopSpeed(next);
    return next;
  }

  // RaceLogic manual controls
  lapStart(): void {
    this.lapTimer.startManual(performance.now());
    this.addLog('lap timer START');
  }

  lapMark(): void {
    this.lapTimer.manualLap(performance.now());
    const st = this.lapTimer.getState();
    this.addLog(`lap timer LAP ${st.lapCount}${st.lastS ? ' ' + st.lastS.toFixed(2) + 's' : ''}`);
  }

  lapStop(): void {
    this.lapTimer.stopManual();
    this.addLog('lap timer STOP');
  }

  lapReset(): void {
    this.lapTimer.reset();
    this.addLog('lap timer RESET');
  }

  setGearbox(mode: ShiftMode): void {
    this.drivetrain.setMode(mode);
    saveSettings({ gearbox: mode });
  }

  getSource(): TelemetrySource {
    return this.source;
  }

  setTelemetrySource(src: TelemetrySource): void {
    if (src === this.source && this.unsubscribe) return;
    this.attachTelemetry(src);
    saveSettings({ source: src });
  }

  toggleTelemetrySource(): TelemetrySource {
    // SIM is a development aid → admin only; drivers get GPS ⇄ CAN
    const order: TelemetrySource[] = isAdmin() ? ['gps', 'can', 'sim'] : ['gps', 'can'];
    if (!order.includes(this.source)) { this.setTelemetrySource(order[0]); return order[0]; }
    const next = order[(order.indexOf(this.source) + 1) % order.length];
    this.setTelemetrySource(next);
    return next;
  }

  setRelayRoom(room: string): void {
    this.remote.setRoom(room);
    saveSettings({ relayRoom: room });
    this.addLog(`relay room → ${room}`);
  }

  toggleDriveMode(): MockDriveMode {
    const next: MockDriveMode = this.mock.mode === 'auto' ? 'manual' : 'auto';
    this.setDriveMode(next);
    return next;
  }

  setDriveMode(mode: MockDriveMode): void {
    this.mock.setMode(mode);
    this.addLog(`drive ${mode === 'auto' ? 'auto-pilot script' : 'manual (keys / touch)'}`);
  }

  setTouchPedals(pedal: number, brake: number): void {
    this.touchPedal = pedal;
    this.touchBrake = brake;
  }

  /** DEMO pit limiter (null to lift). */
  setSpeedCap(kmh: number | null): void {
    if (kmh !== this.demoCapKmh) this.addLog(kmh == null ? 'pit limiter off' : `pit limiter ${kmh} km/h (demo)`);
    this.demoCapKmh = kmh;
    // The demo also holds the box down the gearbox, but the cap has to be high enough for the SPEED
    // cap to be reachable or the demo stops short of its own limit: the 812's second gear tops at 96,
    // under the 100 the limiter now sits at. So it is the lowest gear that can actually get there,
    // never fewer than two.
    if (kmh == null) this.drivetrain.gearCap = null;
    else {
      const tops = this.profile.gearTopSpeedsKmh ?? [];
      const need = tops.findIndex((t) => t >= kmh);
      this.drivetrain.gearCap = Math.max(2, need < 0 ? tops.length : need + 1);
    }
  }

  /** the PIT button: manual pit limiter (null to release) */
  setPitLimiter(kmh: number | null): void {
    if (kmh !== this.manualCapKmh) this.addLog(kmh == null ? 'PIT limiter released' : `PIT limiter ${kmh} km/h`);
    this.manualCapKmh = kmh;
  }

  setVolume(v: number): void {
    this.audio.setVolume(v);
    saveSettings({ volume: v });
  }

  getStatus(): SessionStatus {
    return {
      source: this.source,
      telemetryOk: this.telemetryOk,
      telemetrySource: this.latest?.source ?? this.telemetry.id,
      telemetryHz: this.hz(),
      telemetryQuality: this.telemetry.quality,
      gps: this.gps.getInfo(),
      remote: this.remote.getInfo(),
      driveMode: this.mock.mode,
      gearbox: this.drivetrain.getMode(),
      driveStyle: this.driveStyleSetting,
      resolvedStyle: this.resolvedStyle,
      topSpeedKmh: this.topSpeedKmh,
      pitCap: this.speedCapKmh,
      rawSpeedKmh: this.rawSpeed ?? this.latest?.speedKmh ?? this.state.speedKmh,
      wiperMsg: this.wiperMsg && performance.now() < this.wiperMsg.until ? this.wiperMsg.text : null,
      wet: this.wetFromCar,
      lightsOn: this.lightsOn,
      tires: this.tiresBar,
      lapRunning: this.lapTimer.running,
      lapHasData: this.lapTimer.getState().lapCount > 0 || this.lapTimer.getState().lastS != null,
      audio: this.audio.getStatus(),
      lap: this.lapTimer.getState(),
      mediaSession: this.mediaSessionOk,
      wakeLock: isWakeLockActive(),
      started: this.started,
      engine: this.engine,
      entitlement: this.grant?.mode ?? null,
    };
  }

  // ---- loop --------------------------------------------------------------

  private attachTelemetry(src: TelemetrySource): void {
    this.unsubscribe?.();
    this.telemetry.stop();
    this.source = src;
    this.telemetry = src === 'gps' ? this.gps : src === 'can' ? this.remote : this.mock;
    this.latest = null;
    this.frameTimes = [];
    this.unsubscribe = this.telemetry.subscribe((f) => this.onFrame(f));
    void this.telemetry.start();
    // the lap timer needs GPS positions whatever drives the engine: keep the GPS provider alive on CAN too
    this.gpsSideUnsub?.();
    this.gpsSideUnsub = null;
    this.latestGps = null;
    this.lastGpsAt = 0;
    if (src === 'can') {
      void this.gps.start();
      // side-listen to the browser GPS: when the CAN goes quiet for a moment, its speed keeps the engine honest
      this.gpsSideUnsub = this.gps.subscribe((f) => { this.latestGps = f; this.lastGpsAt = performance.now(); });
    } else if (src !== 'gps') this.gps.stop();
    // inside the app: the HEADLESS hardware bridge follows the source (Marc: no separate screen) —
    // picking Pro hardware powers the Commander/RaceBox/OBD machinery, leaving it powers it down
    try { (window as Window & { RoarBridge?: { postMessage: (m: string) => void } }).RoarBridge?.postMessage(src === 'can' ? 'on' : 'off'); } catch { /* not in the app */ }
    // SIM = you drive with the keys (↑ gas ↓ brake ← → gears); K switches to the auto-pilot script
    if (src === 'sim' && this.mock.mode !== 'manual') this.mock.setMode('manual');
    this.addLog(`telemetry source → ${src}`);
  }

  private onFrame(f: TelemetryFrame): void {
    this.latest = f;
    this.lastFrameAt = performance.now();
    this.frameTimes.push(this.lastFrameAt);
    if (this.frameTimes.length > 40) this.frameTimes.shift();
    // The car's own engine rpm only ever arrives from an OBD-II dongle, and when it does the driver's
    // real gearbox can be measured instead of guessed from a catalogue of typical figures. Nothing in
    // the sound depends on this; it feeds the pit box.
    if (typeof f.engineRpm === 'number' && f.engineRpm > 0) {
      this.gearLearner.feed(f.speedKmh, f.engineRpm, this.lastFrameAt, f.speedAgeMs ?? 0, f.engineRpmAgeMs ?? 0);
      this.saveGearLearning();
      // …and once it knows the box, the engine stops deciding when to change gear and simply changes when
      // the driver does (Marc: 'cuando ellos cambien de marcha, nosotros también'). Inside the gear it
      // still stretches its own range with speed, so the car sings instead of mumbling along at 2000 rpm.
      this.lastEngineRpmAt = this.lastFrameAt;
      this.drivetrain.followGear = this.followMyGears ? (this.gearLearner.engagedGear(this.lastFrameAt) || null) : null;
    }
  }

  /** The gear the driver's own car is in, when we can see it (0 = we cannot). */
  followingGear(): number {
    return this.drivetrain.followGear ?? 0;
  }

  /**
   * Whether the engine copies the driver's own gearbox. It is ON by default because it is simply better
   * than any ladder, and it can only act when it recognises the gear. Switching it off (the dash, or the
   * pit box) hands the box back its own judgement and brings the paddles back.
   */
  isFollowingMyGears(): boolean { return this.followMyGears; }

  setFollowMyGears(on: boolean): void {
    this.followMyGears = on;
    saveSettings({ followMyGears: on });
    if (!on) this.drivetrain.followGear = null;
  }

  /** What the learner knows so far (the pit box shows it, and offers it once it is sure). */
  gearLearning(sim = false): LearnState {
    return (sim ? this.simLearner : this.gearLearner).state;
  }

  gearLearningLadder(sim = false): number[] {
    return (sim ? this.simLearner : this.gearLearner).ladder();
  }

  resetGearLearning(): void {
    this.gearLearner.reset();
    try { localStorage.removeItem(GEARS_KEY); } catch { /* private mode */ }
  }

  /** Learning survives the drive: a commute at a time is exactly how the middle gears fill up. */
  private saveGearLearning(): void {
    const now = performance.now();
    if (now - this.gearsSavedAt < 10000) return;
    this.gearsSavedAt = now;
    try { localStorage.setItem(GEARS_KEY, JSON.stringify(this.gearLearner.toJSON())); } catch { /* private mode */ }
  }

  private hz(): number {
    if (this.frameTimes.length < 2) return 0;
    const span = this.frameTimes[this.frameTimes.length - 1] - this.frameTimes[0];
    return span > 0 ? Math.round(((this.frameTimes.length - 1) / span) * 1000) : 0;
  }

  private scheduleLoop(): void {
    cancelAnimationFrame(this.rafId);
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
    if (document.visibilityState === 'visible') {
      const frame = () => {
        if (!this.running) return;
        this.tick();
        this.rafId = requestAnimationFrame(frame);
      };
      this.rafId = requestAnimationFrame(frame);
    } else {
      this.intervalId = setInterval(() => this.tick(), 33);
    }
  }

  private onVisibility = (): void => {
    this.addLog(`visibility ${document.visibilityState}`);
    this.scheduleLoop();
    if (document.visibilityState === 'visible') void this.audio.resume();
  };

  private grantRefreshing = false;
  /** DEMO tokens are short-lived: when one expires, ask the server again — 'none' ends the demo for today. */
  private watchGrant(): void {
    const g = this.grant;
    if (!g || g.mode !== 'demo' || this.grantRefreshing || Date.now() < g.exp) return;
    this.grantRefreshing = true;
    void this.refreshGrant().finally(() => { this.grantRefreshing = false; });
  }

  private tick(): void {
    this.watchGrant();
    const now = performance.now();
    const dt = Math.min(0.1, Math.max(0.001, (now - this.lastTick) / 1000));
    this.lastTick = now;

    this.keyboard.tick(dt);
    if (this.source === 'sim' && this.mock.mode === 'manual') {
      const pedal = Math.max(this.keyboard.pedal, this.touchPedal);
      // a car with its engine off does not drive: the sim used to roll to 90 km/h in silence at 0 rpm.
      // The throttle starts it instead (a real drive does the same the moment the car moves), and the
      // car waits for the engine to catch before it pulls away
      if (pedal > 0 && this.engine === 'off' && this.started) this.engineStart();
      this.mock.manualPedal = this.engine === 'running' ? pedal : 0;
      this.mock.manualBrake = Math.max(this.keyboard.brake, this.touchBrake);
    }

    // telemetry watchdog: hold, then decay to idle — never stop the audio.
    // CAN goes stale after 800 ms (the relay stutters in bursts); the browser GPS then covers the gap.
    const timeout = this.source === 'can' ? 800 : TELEMETRY_TIMEOUT_MS;
    const canStale = this.source === 'can' && (this.latest === null || now - this.lastFrameAt >= timeout);
    const gpsCover = canStale && this.latestGps !== null && now - this.lastGpsAt < 2500;
    // TESLA SCREEN + COMMANDER (Marc's combo): the Commander's own speed arrives a second or two late,
    // while the page's geolocation in a car browser IS the car's, measured at 10 Hz. So the fast one owns
    // the speed and the CAN keeps what only it knows — gear, pedal, brake, launch. Excluded in the app,
    // where the page GPS is the PHONE's, and stood down whenever the relay carries a RaceBox of its own.
    const fastPageGps = this.source === 'can'
      && !GeolocationTelemetryProvider.nativeBridge
      && !this.latest?.gps
      && this.latestGps !== null && now - this.lastGpsAt < 1200
      && this.gps.estimator.medianGapMs < 400;
    const fresh = (this.latest !== null && now - this.lastFrameAt < timeout) || gpsCover;
    if (fresh !== this.telemetryOk) {
      this.telemetryOk = fresh;
      this.addLog(fresh ? `telemetry ok (${gpsCover ? 'can+gps' : this.telemetry.id})` : 'telemetry lost — holding');
    }
    if (this.source === 'can') {
      const from = gpsCover || fastPageGps ? 'gps' : 'can';
      if (from !== this.speedFrom) { this.speedFrom = from; this.addLog(from === 'gps' ? (fastPageGps ? 'car-screen gps owns the speed — can keeps gear/pedals' : 'can quiet — gps speed covering') : 'can speed back'); }
    }
    let input;
    if (gpsCover && this.latestGps) {
      // CAN hiccup: the browser GPS drives the speed; gear/pedal/brake hold their last CAN values
      this.heldSpeed = this.latestGps.speedKmh;
      input = { speedKmh: this.latestGps.speedKmh, accelMs2: this.latestGps.accelMs2, pedal: this.latest?.pedal ?? null, brake: this.latest?.brake ?? null };
    } else if (fresh && this.latest) {
      // the car screen's own GPS beats the Commander's laggy DI_speed; everything else stays CAN
      const g = fastPageGps ? this.latestGps : null;
      this.heldSpeed = g ? g.speedKmh : this.latest.speedKmh;
      input = { speedKmh: this.heldSpeed, accelMs2: g ? g.accelMs2 : this.latest.accelMs2, pedal: this.latest.pedal, brake: this.latest.brake };
      if (this.source === 'can') {
        // Marc's CAN rules: P / R / N → neutral (N on the dash, pedal revs the engine); D → 1st engaged, road speed is the master
        const g = this.latest.gear ?? null;
        if (this.latest.tires) this.tiresBar = this.latest.tires;
        if (this.latest.gps) {
          // RaceBox 25 Hz position through the bridge — RaceLogic-grade lap timing; the browser's own
          // 1 Hz GPS stands down while this is fresh (see the onPosition guard)
          this.lapTimer.update({ lat: this.latest.gps.lat, lon: this.latest.gps.lon }, now);   // performance.now() base, same clock as the browser-GPS path
          this.lastRelayGpsAt = now;
        }
        const neutral = g === 'P' || g === 'N' || g === 'R';
        this.drivetrain.selector = g;
        if (neutral !== this.drivetrain.neutral) {
          this.drivetrain.neutral = neutral;
          this.addLog(`can gear ${g ?? '?'} → ${neutral ? 'neutral' : 'drive'}`);
          // Marc: shifting the Tesla into D with the engine off cranks it right away (P keeps it quiet in N)
          if (!neutral && this.engine === 'off' && this.started) this.engineStart();
          // N → D is a real gear engagement in the car, so the box should be heard selecting first
          // (Marc: 'cuando está en neutro y pasa a 1, debería sonar el cambio de marcha a 1')
          if (!neutral && this.engine !== 'off') this.audio.handleEvent({ type: 'shiftUp', fromGear: 0, toGear: 1, rpm: this.state.rpm }, this.state);
        }
        // launch control from the car's pedals lives in the drivetrain now: the brake held to the floor for
        // 5 s at a standstill arms it, and the brake can come off once it is armed (Marc)
      }
    } else {
      // coast down gracefully: no pedal, no brake, no inferred deceleration
      this.heldSpeed = Math.max(0, this.heldSpeed - HOLD_DECAY_KMH_PER_S * dt);
      input = { speedKmh: this.heldSpeed, accelMs2: 0, pedal: 0, brake: 0 };
    }

    // what the CAR is really doing, before the limiter touches it: the pit page shows this number, not
    // the capped one (Marc: 'la velocidad tendría que ser la actual'). On the Tesla's own screen there is
    // no CAN sample to fall back on, so the dash was reading the cap back to itself: a permanent 59.
    this.rawSpeed = input.speedKmh;
    const cap = this.speedCapKmh;
    this.drivetrain.pitLimiterOn = cap != null && input.speedKmh > cap - 0.5;
    if (cap != null && input.speedKmh > cap) {
      // pit limiter: the car may go faster, the virtual engine does not
      input = { ...input, speedKmh: cap, accelMs2: Math.min(0, input.accelMs2 ?? 0) };
    }
    if (this.engine === 'off' && this.started && input.speedKmh > AUTO_IGNITION_KMH && this.source !== 'sim') this.engineStart();
    // hand the long real start clip over to the voice: at its tail, or the moment the car moves / pedal in
    if (this.starterEndsAt > 0 && this.engine === 'running') {
      const interrupt = input.speedKmh > 3 || (input.pedal ?? 0) > 0.2 || this.state.launch;
      if (interrupt || now >= this.starterEndsAt) {
        this.audio.setEngineRunning(true, interrupt ? 0.25 : 1.6);
        this.starterHandle?.stop(interrupt ? 0.35 : 4.0);
        this.starterHandle = null;
        this.starterEndsAt = 0;
      }
    }
    // the dongle went quiet (unplugged, ignition off, a car that stopped answering): the box takes its
    // own decisions again rather than freezing in whatever gear it last saw
    if (this.drivetrain.followGear != null && now - this.lastEngineRpmAt > 3000) this.drivetrain.followGear = null;
    this.state = this.drivetrain.tick(input, dt);
    // the simulator drives a real gearbox too, so it can prove the learner end to end: drive the SIM and
    // the ladder that comes back should be this car's own (Marc has no dongle to test with)
    if (this.source === 'sim') this.simLearner.feed(this.state.speedKmh, this.state.rpm, now, 0, 0);
    if (this.state.launch !== this.wasLaunch) {
      this.wasLaunch = this.state.launch;
      this.addLog(this.state.launch ? 'LAUNCH armed' : 'launch off');
      // launch means RACE (Marc): arming it switches the box over if the driver was in DRIVE
      if (this.state.launch && this.resolvedStyle !== 'sport') this.setDriveStyle('sport');
    }
    this.audio.update(this.state, dt);
    this.audio.setLaunchBoost(this.state.launch);   // AMS2 slider to max while launch control holds the revs
    this.lapTimer.tick(now);

    // LAPS DO NOT LEAVE THE CAR (Marc, 2026-09-02: "las vueltas tienen q ser de cada usuario en cada
    // sesion, cuando cierra sesion se borran"). They live in lapTimer, in memory, and die with the
    // session — which is what the driver already saw on the dash. Until today every completed lap was
    // ALSO posted to /api/lap and kept for ever against the device id, and nothing ever read it back:
    // /api/laps/best had no caller anywhere in the app. A write-only table of somebody's driving is a
    // retention liability for zero product, so the post is gone and so are both endpoints.

    // AUTO drive-style detection: hard acceleration/braking pushes toward sport,
    // calm driving decays toward eco (with hysteresis so it doesn't flap).
    if (this.driveStyleSetting === 'auto') {
      const a = Math.abs(this.state.accelMs2);
      const hard = a > 2.2 || this.state.throttleIntent > 0.75;
      this.aggressiveness = Math.max(0, Math.min(1, this.aggressiveness + (hard ? dt * 0.9 : -dt * 0.06)));
      const want: DriveStyle = this.resolvedStyle === 'sport' ? (this.aggressiveness < 0.35 ? 'eco' : 'sport') : this.aggressiveness > 0.65 ? 'sport' : 'eco';
      if (want !== this.resolvedStyle) {
        this.resolvedStyle = want;
        this.drivetrain.setDriveStyle(want);
        this.addLog(`style → ${want} (auto)`);
      }
    }
  }

  addLog(msg: string): void {
    const t = new Date();
    const ts = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
    this.log.push(`${ts} ${msg}`);
    if (this.log.length > 120) this.log.shift();
  }
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ---- registry so navigation keeps the engine running ----------------------
let current: Session | null = null;

export function getSession(profile: CarProfile): Session {
  if (current && current.profile.id === profile.id) return current;
  current?.destroy();
  current = new Session(profile);
  current.run();
  saveSettings({ lastCar: profile.id });
  // debugging hook (no devtools in the Tesla browser; also used by e2e checks)
  (window as Window & { __roardrive?: Session }).__roardrive = current;
  return current;
}

export function currentSession(): Session | null {
  return current;
}
